/**
 * Logger that writes to an in-memory buffer for download as a log file.
 * The in-memory buffer is capped to ~10 MB; when the cap is exceeded, the oldest entries are
 * evicted when exceeded. In development, entries are also written to console; in production,
 * only the buffer is used (no console output). Use for quote Excel upload and traceability.
 *
 * Usage conventions (context names, levels, download flow): see repository `docs/logging.md`.
 */

const LEVELS = { info: 'INFO', warn: 'WARN', error: 'ERROR' };
const MAX_BUFFER_LINES = 1000; // secondary guard on line count
const MAX_BUFFER_BYTES = 10 * 1024 * 1024; // ~10 MB
const logBuffer = [];
let currentBufferBytes = 0;
let bufferEnabled = true;

const isProduction = () =>
  typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';

function formatEntry(level, context, message, data) {
  const ts = new Date().toISOString();
  const dataStr = data != null ? ' ' + JSON.stringify(data) : '';
  return `[${ts}] [${level}] [${context}] ${message}${dataStr}\n`;
}

function appendToBuffer(entry) {
  if (!bufferEnabled) return;
  // Approximate UTF-16 string size: 2 bytes per code unit
  const entrySize = entry.length * 2;
  logBuffer.push(entry);
  currentBufferBytes += entrySize;

  // Trim based on total buffer size first
  while (currentBufferBytes > MAX_BUFFER_BYTES && logBuffer.length > 0) {
    const removed = logBuffer.shift();
    currentBufferBytes -= removed.length * 2;
  }

  // Secondary guard: prevent unbounded line count growth
  while (logBuffer.length > MAX_BUFFER_LINES) {
    const removed = logBuffer.shift();
    currentBufferBytes -= removed.length * 2;
  }
}

/**
 * Log an info message.
 * @param {string} context - Module or feature name (e.g. 'excelQuoteParser').
 * @param {string} message - Short description.
 * @param {Object} [data] - Optional object to log (will be JSON.stringify'd).
 */
export function logInfo(context, message, data) {
  const entry = formatEntry(LEVELS.info, context, message, data);
  if (!isProduction()) console.info(entry);
  appendToBuffer(entry);
}

/**
 * Log a warning.
 * @param {string} context - Module or feature name.
 * @param {string} message - Short description.
 * @param {Object} [data] - Optional object to log.
 */
export function logWarn(context, message, data) {
  const entry = formatEntry(LEVELS.warn, context, message, data);
  if (!isProduction()) console.warn(entry);
  appendToBuffer(entry);
}

/**
 * Log an error.
 * @param {string} context - Module or feature name.
 * @param {string} message - Short description.
 * @param {Object|Error} [data] - Optional error or object (message/stack preserved).
 */
export function logError(context, message, data) {
  const payload = data instanceof Error
    ? { message: data.message, name: data.name }
    : data;
  const entry = formatEntry(LEVELS.error, context, message, payload);
  if (!isProduction()) console.error(entry);
  appendToBuffer(entry);
}

/**
 * Convenience helper for logging infinite scroll debug events.
 * @param {string} context - Logical context (e.g. 'suppliersPage', 'supplierTable').
 * @param {string} message - Event label or short description.
 * @param {Object} [data] - Optional structured metadata for the event.
 */
export function logInfiniteScrollDebug(context, message, data) {
  logInfo(`infiniteScroll:${context}`, message, data);
}

/**
 * Generic list/search logging helpers for tables and infinite scroll.
 * These write into the shared buffer under the unified 'listSearch' context.
 */
export function logListSearchEvent(entity, phase, payload) {
  const safeEntity = entity || 'unknown';
  const safePhase = phase || 'unknown';
  const data = payload || {};
  const message = `${safeEntity}:${safePhase}`;
  logInfo('listSearch', message, data);
}

export function logListSearchError(entity, trigger, error, payload) {
  const safeEntity = entity || 'unknown';
  const safeTrigger = trigger || 'unknown';
  const base = payload || {};
  const errInfo = error
    ? { errorName: error.name, errorMessage: error.message }
    : {};
  const message = `${safeEntity}:failure:${safeTrigger}`;
  logError('listSearch', message, { ...base, ...errInfo });
}

/**
 * Download the current in-memory log buffer as a text file.
 * @param {string} [filename] - Optional filename (default: quote-upload-log-{timestamp}.txt).
 */
export function downloadLogFile(filename) {
  const name = filename || `quote-upload-log-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
  const content = logBuffer.length ? logBuffer.join('') : 'No log entries.';
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Clear the in-memory log buffer.
 */
export function clearLogBuffer() {
  logBuffer.length = 0;
  currentBufferBytes = 0;
}

/**
 * Enable or disable writing to the in-memory buffer (console unchanged).
 * @param {boolean} enabled
 */
export function setBufferEnabled(enabled) {
  bufferEnabled = !!enabled;
}

export const listSearchLogger = {
  logEvent: logListSearchEvent,
  logError: logListSearchError,
};

const logger = {
  logInfo,
  logWarn,
  logError,
  logInfiniteScrollDebug,
  logListSearchEvent,
  logListSearchError,
  downloadLogFile,
  clearLogBuffer,
  setBufferEnabled,
  listSearchLogger,
};
export default logger;
